import {
  useIsS,
  useIsXS,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSText
} from "@wingspanhq/fe-component-library";
import classNames from "classnames";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useBrowserPageTitle } from "../../hooks/useBrowserPageTitle";
import { AppFooter } from "../AppFooter";

import { Navigation } from "./Navigation";
import { Stepper } from "./Stepper";
import styles from "./index.module.scss";
import { FlowStep } from "../FlowSwitch/types";

export type LayoutProps = {
  title?: string;
  onClose?: () => void;
  onBack?: () => void;
  navigation?: FlowStep[];
  customHeader?: React.ReactNode;
  hideHeader?: boolean;
  wide?: boolean;
  /**
   * Widens the centered content column for wide-form hosts (e.g. the
   * EmployeeTaxForms dynamic-form wizard, whose rail + content canvas is
   * 944px): 944px without navigation, 1264px with the navigation rail so the
   * 9/12 content grid item still resolves to 944px. Defaults to the existing
   * widths (760px / 956px with navigation), so consumers that don't pass it
   * are unaffected.
   */
  wideContent?: boolean;
  showFooterLinks?: boolean;
  showFooterAdditionalInfo?: boolean;
};

export const Layout: React.FC<LayoutProps> = ({
  title,
  onClose,
  onBack,
  navigation,
  wide,
  wideContent,
  customHeader,
  hideHeader,
  showFooterLinks = true,
  showFooterAdditionalInfo = false,
  children
}) => {
  useBrowserPageTitle(title);
  const isXS = useIsXS();
  const isS = useIsS();
  const { pathname } = useLocation();
  const layoutRef = useRef<HTMLDivElement>(null);

  const hasNavigation = useMemo(
    () => navigation && navigation.length > 0,
    [navigation]
  );

  // The layout element is the scroll container (position: fixed, overflow-y:
  // auto), so navigating between steps/pages leaves it scrolled wherever the
  // previous page ended. Reset it to the top on every route change.
  useEffect(() => {
    layoutRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const topHeaderPadding =
    window.document
      .querySelector("[data-headerheight]")
      ?.getAttribute("data-headerheight") || "0";

  return (
    <WSElement
      ref={layoutRef}
      className={styles.layout}
      style={{ paddingTop: topHeaderPadding + "px" }}
    >
      {customHeader ? (
        customHeader
      ) : hideHeader ? null : (
        <WSFlexBox.CenterY className={styles.header} px="XL" py="L">
          {onBack ? (
            <WSIcon name="arrow-left" onClick={onBack} />
          ) : (
            <WSElement />
          )}

          <WSFlexBox.Center className={styles.headerCenter}>
            <WSText.ParagraphSm weight="medium">{title}</WSText.ParagraphSm>
          </WSFlexBox.Center>

          {onClose ? <WSIcon name="exit" onClick={onClose} /> : <WSElement />}
        </WSFlexBox.CenterY>
      )}

      <WSElement
        className={classNames(
          styles.content,
          hasNavigation && styles.twoColumn,
          wide && styles.threeColumn
        )}
        // Wide-form hosts override the column cap inline (944px plain; 1264px
        // with the navigation rail so the 9/12 content grid item resolves to
        // 944px: (9C - 3 * 16px gutter) / 12 = 944 => C = 1264). Inline so the
        // shared stylesheet stays untouched; defaults apply when unset.
        style={
          wideContent ? { maxWidth: hasNavigation ? 1264 : 944 } : undefined
        }
      >
        {hasNavigation ? (
          <WSGrid>
            <WSGrid.Item span={{ m: "3" }}>
              {isXS || isS ? (
                <Stepper mb="L" navigation={navigation || []} />
              ) : (
                <Navigation navigation={navigation || []} />
              )}
            </WSGrid.Item>
            <WSGrid.Item span={{ m: "9" }}>{children}</WSGrid.Item>
          </WSGrid>
        ) : (
          children
        )}
      </WSElement>
      <AppFooter
        showLinks={showFooterLinks}
        showAdditionalInfo={showFooterAdditionalInfo}
      />
    </WSElement>
  );
};
